import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MessageCircle,
  Mic,
  FileText,
  ShieldCheck,
  Languages,
  Stethoscope,
  Heart,
  Sparkles,
  Phone,
  Brain,
  ClipboardList,
  TrendingUp,
  Lock,
  AlertTriangle,
  HelpCircle,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LANGS, t } from '../i18n';

const LANDING_COPY = {
  en: {
    heroAlt: 'Community care',
    listeningTitle: 'Buddy is listening',
    listeningSub: 'Voice + text, any language',
    whyBadge: 'Built for real care handoffs',
    whyTitle: 'Not generic advice. A clearer story for the doctor.',
    whySub: 'Health Buddy listens like a local health assistant, asks only the next useful question, and turns the conversation into a structured intake summary. The goal is simple: help patients explain better and help doctors review faster.',
    whyCards: [
      ['Context-aware intake', 'Buddy follows the symptom story, age, language, history, and warning signs instead of giving one-size-fits-all answers.'],
      ['Doctor-ready summary', 'Every report captures symptoms, severity, red flags, safe comfort steps, and doctor review guidance in one place.'],
      ['Local-language comfort', 'Patients can speak naturally in English, Telugu, Hindi, or Tamil, including everyday mixed-language phrasing.'],
      ['Safety boundaries', 'Health Buddy does not diagnose or prescribe. Serious symptoms are escalated toward urgent medical care.'],
    ],
    useTitle: 'Everyday moments where Health Buddy helps.',
    useSub: 'From a late-night worry to a doctor visit, the app keeps the next step understandable.',
    useCards: [
      ['Know when to act fast', 'Chest pain, breathing trouble, fainting, heavy bleeding, stroke signs, and other red flags are treated as urgent.'],
      ['Carry a clean PDF', 'Patients, agents, and doctors can use the PDF summary instead of relying on memory or scattered chat messages.'],
      ['Prepare for doctor review', 'The report helps patients discuss duration, severity, medicines, history, and questions more clearly during consultation.'],
      ['Build useful history', 'Website and WhatsApp reports connect by phone number, so repeated visits can show past summaries in one account.'],
      ['Support field agents', 'Agents can help patients start on WhatsApp, open links, download PDFs, and share structured summaries for review.'],
      ['Ask simple questions', 'Patients can ask what a symptom may mean, what to track, and what to tell a doctor without feeling judged.'],
    ],
    safetyTitle: 'Private, consent-led, and built for handoff.',
    safetySub: 'Health Buddy keeps the patient in control. WhatsApp report links are signed and time-limited, website reports require login, and doctor access is limited to assigned referrals.',
    safetyItems: [
      'Signed WhatsApp links for report and PDF access',
      'Login-based history for website patients',
      'Doctor portal for assigned report review',
      'Clear disclaimer: not diagnosis or prescription',
    ],
    capturesTitle: 'What the report captures.',
    captures: [
      'Chief complaint, duration, location, and severity',
      'Associated symptoms, red flags, and urgency guidance',
      'Past illness, medicines, allergies, and relevant habits when reported',
      'Safe comfort steps and what to track before doctor review',
      'PDF summary that agents and doctors can open from WhatsApp or the site',
    ],
    waBot1: 'Hi! I am Health Buddy. How are you feeling today?',
    waUser: 'Fever since 2 days, tired.',
    waBot2: "Okay, I'm here. Can you tell me if you also have cough or body pain?",
    faqTitle: 'Questions patients and partners ask.',
    faqSub: 'Short, clear answers before someone starts a consultation.',
    faqs: [
      ['Is Health Buddy a doctor?', 'No. Health Buddy is an AI intake assistant. It helps collect symptoms and prepare a doctor-ready summary, but it does not replace medical advice.'],
      ['Can it give prescriptions?', 'No. It avoids medicine dosage and final diagnosis. Doctors can review reports and respond through the doctor portal.'],
      ['Can patients use it only on WhatsApp?', 'Yes. Patients can complete intake on WhatsApp and receive a report link plus PDF link. They can also login on the website with the same phone number.'],
      ['How does it help agents?', 'Agents can guide patients through WhatsApp, open the generated report, download the PDF, and help connect the patient with a doctor.'],
      ['What happens in an emergency?', 'If serious warning signs are mentioned, Health Buddy asks the patient to seek urgent medical care or call emergency services.'],
    ],
  },
  hi: {
    heroAlt: 'समुदाय स्वास्थ्य सेवा',
    listeningTitle: 'Buddy सुन रहा है',
    listeningSub: 'आवाज़ + टेक्स्ट, किसी भी भाषा में',
    whyBadge: 'असली देखभाल और डॉक्टर हैंडऑफ के लिए',
    whyTitle: 'सामान्य सलाह नहीं. डॉक्टर के लिए साफ कहानी.',
    whySub: 'Health Buddy स्थानीय स्वास्थ्य सहायक की तरह सुनता है, एक बार में सही अगला सवाल पूछता है, और बातचीत को डॉक्टर के लिए व्यवस्थित सारांश में बदलता है.',
    whyCards: [
      ['संदर्भ समझने वाला intake', 'Buddy लक्षण, उम्र, भाषा, इतिहास और warning signs को देखकर सवाल पूछता है.'],
      ['डॉक्टर-ready summary', 'हर रिपोर्ट में लक्षण, गंभीरता, red flags, safe steps और doctor guidance एक जगह रहती है.'],
      ['स्थानीय भाषा में आराम', 'मरीज़ English, Telugu, Hindi या Tamil में स्वाभाविक रूप से बोल सकते हैं.'],
      ['सुरक्षा सीमाएं', 'Health Buddy diagnosis या prescription नहीं देता. गंभीर लक्षणों पर urgent care की सलाह देता है.'],
    ],
    useTitle: 'जहां रोज़ Health Buddy मदद करता है.',
    useSub: 'रात की चिंता से लेकर डॉक्टर visit तक, अगला कदम समझना आसान रहता है.',
    useCards: [
      ['कब तुरंत action लेना है', 'Chest pain, सांस की तकलीफ, बेहोशी, bleeding और stroke signs urgent माने जाते हैं.'],
      ['साफ PDF साथ रखें', 'मरीज़, agents और doctors memory के बजाय PDF summary इस्तेमाल कर सकते हैं.'],
      ['Doctor review की तैयारी', 'रिपोर्ट duration, severity, medicines, history और questions साफ बताने में मदद करती है.'],
      ['Useful history बनाएं', 'Website और WhatsApp reports phone number से जुड़कर past summaries दिखा सकते हैं.'],
      ['Field agents को support', 'Agents WhatsApp शुरू कराने, links खोलने, PDF download और doctor sharing में मदद कर सकते हैं.'],
      ['सरल सवाल पूछें', 'मरीज़ symptom का मतलब, क्या track करना है, और doctor को क्या बताना है पूछ सकते हैं.'],
    ],
    safetyTitle: 'Private, consent-led, और handoff के लिए बनाया गया.',
    safetySub: 'Health Buddy patient को control देता है. WhatsApp links signed/time-limited हैं, website reports login मांगती हैं, और doctor access assigned referrals तक सीमित है.',
    safetyItems: ['Report/PDF के लिए signed WhatsApp links', 'Website patients के लिए login-based history', 'Assigned report review के लिए doctor portal', 'Clear disclaimer: diagnosis या prescription नहीं'],
    capturesTitle: 'Report क्या capture करती है.',
    captures: ['मुख्य शिकायत, duration, location और severity', 'Associated symptoms, red flags और urgency guidance', 'Past illness, medicines, allergies और habits जब बताए जाएं', 'Safe comfort steps और doctor review से पहले क्या track करें', 'WhatsApp या site से खुलने वाली PDF summary'],
    waBot1: 'नमस्ते! मैं Health Buddy हूं. आज कैसा महसूस हो रहा है?',
    waUser: '2 दिन से fever है, थकान है.',
    waBot2: 'ठीक है, मैं हूं. क्या cough या body pain भी है?',
    faqTitle: 'Patients और partners के सवाल.',
    faqSub: 'Consultation शुरू करने से पहले छोटे और साफ जवाब.',
    faqs: [
      ['क्या Health Buddy doctor है?', 'नहीं. यह AI intake assistant है. Symptoms collect करके doctor-ready summary बनाता है, medical advice replace नहीं करता.'],
      ['क्या prescription देता है?', 'नहीं. Dosage और final diagnosis से बचता है. Doctors portal से report review कर सकते हैं.'],
      ['क्या WhatsApp पर ही use कर सकते हैं?', 'हां. WhatsApp पर intake पूरा करके report और PDF link मिल सकता है.'],
      ['Agents को कैसे मदद करता है?', 'Agents patient को WhatsApp flow, report link, PDF download और doctor connect में guide कर सकते हैं.'],
      ['Emergency में क्या होता है?', 'Serious warning signs पर Health Buddy urgent medical care या emergency services की सलाह देता है.'],
    ],
  },
  te: {
    heroAlt: 'కమ్యూనిటీ కేర్',
    listeningTitle: 'Buddy వినుతోంది',
    listeningSub: 'వాయిస్ + టెక్స్ట్, ఏ భాషలోనైనా',
    whyBadge: 'నిజమైన care handoff కోసం',
    whyTitle: 'సాధారణ సలహా కాదు. డాక్టర్‌కు స్పష్టమైన కథ.',
    whySub: 'Health Buddy స్థానిక health assistant లాగా వింటుంది, అవసరమైన తదుపరి ప్రశ్న మాత్రమే అడుగుతుంది, conversation ను doctor-ready summary గా మార్చుతుంది.',
    whyCards: [
      ['Context-aware intake', 'లక్షణాలు, వయసు, భాష, history, warning signs చూసి Buddy సరైన ప్రశ్నలు అడుగుతుంది.'],
      ['Doctor-ready summary', 'ప్రతి report లో symptoms, severity, red flags, safe steps, doctor guidance ఒకేచోట ఉంటాయి.'],
      ['స్థానిక భాషలో సౌకర్యం', 'English, Telugu, Hindi, Tamil లేదా mixed language లో natural గా మాట్లాడవచ్చు.'],
      ['Safety boundaries', 'Health Buddy diagnosis లేదా prescription ఇవ్వదు. Serious symptoms అయితే urgent care కి guide చేస్తుంది.'],
    ],
    useTitle: 'రోజువారీ సందర్భాల్లో Health Buddy సహాయం.',
    useSub: 'రాత్రి worry నుండి doctor visit వరకు, next step సులభంగా అర్థమవుతుంది.',
    useCards: [
      ['ఎప్పుడు త్వరగా act చేయాలో తెలుసుకోండి', 'Chest pain, breathing trouble, fainting, bleeding, stroke signs urgent గా తీసుకుంటుంది.'],
      ['Clean PDF తీసుకెళ్లండి', 'Patients, agents, doctors memory మీద కాకుండా PDF summary వాడవచ్చు.'],
      ['Doctor review కి సిద్ధం', 'Duration, severity, medicines, history, questions స్పష్టంగా చెప్పడానికి report సహాయం చేస్తుంది.'],
      ['Useful history build చేయండి', 'Website మరియు WhatsApp reports phone number తో connect అయ్యి past summaries చూపిస్తాయి.'],
      ['Field agents కి support', 'Agents WhatsApp start చేయడం, links open చేయడం, PDF download, doctor sharing లో help చేస్తారు.'],
      ['సింపుల్ ప్రశ్నలు అడగండి', 'Symptom అర్థం, ఏమి track చేయాలి, doctor కి ఏమి చెప్పాలి అని అడగవచ్చు.'],
    ],
    safetyTitle: 'Private, consent-led, handoff కోసం built.',
    safetySub: 'Health Buddy patient control లో ఉంచుతుంది. WhatsApp report links signed/time-limited, website reports login తో, doctor access assigned referrals కి మాత్రమే.',
    safetyItems: ['Report/PDF access కోసం signed WhatsApp links', 'Website patients కోసం login-based history', 'Assigned report review కోసం doctor portal', 'Clear disclaimer: diagnosis/prescription కాదు'],
    capturesTitle: 'Report ఏమి capture చేస్తుంది.',
    captures: ['Chief complaint, duration, location, severity', 'Associated symptoms, red flags, urgency guidance', 'Past illness, medicines, allergies, habits చెప్పినప్పుడు', 'Doctor review ముందు safe comfort steps మరియు track చేయాల్సినవి', 'WhatsApp లేదా site లో open అయ్యే PDF summary'],
    waBot1: 'హాయ్! నేను Health Buddy. ఈరోజు ఎలా feel అవుతున్నారు?',
    waUser: '2 రోజులుగా fever, tired గా ఉంది.',
    waBot2: 'సరే, నేను ఉన్నాను. cough లేదా body pain కూడా ఉందా?',
    faqTitle: 'Patients మరియు partners అడిగే ప్రశ్నలు.',
    faqSub: 'Consultation మొదలు పెట్టే ముందు short, clear answers.',
    faqs: [
      ['Health Buddy doctor ఆ?', 'కాదు. ఇది AI intake assistant. Symptoms collect చేసి doctor-ready summary తయారు చేస్తుంది.'],
      ['Prescription ఇస్తుందా?', 'కాదు. Dosage మరియు final diagnosis ఇవ్వదు. Doctors portal ద్వారా review చేస్తారు.'],
      ['WhatsApp లోనే వాడొచ్చా?', 'అవును. WhatsApp లో intake పూర్తి చేసి report link మరియు PDF link పొందవచ్చు.'],
      ['Agents కి ఎలా help?', 'Agents WhatsApp flow, report link, PDF download, doctor connect లో guide చేస్తారు.'],
      ['Emergency లో ఏమవుతుంది?', 'Serious warning signs ఉంటే urgent medical care లేదా emergency services కి guide చేస్తుంది.'],
    ],
  },
  ta: {
    heroAlt: 'சமூக சுகாதார சேவை',
    listeningTitle: 'Buddy கேட்கிறது',
    listeningSub: 'குரல் + உரை, எந்த மொழியிலும்',
    whyBadge: 'உண்மையான care handoff க்காக',
    whyTitle: 'பொது அறிவுரை அல்ல. மருத்துவருக்கு தெளிவான கதை.',
    whySub: 'Health Buddy உள்ளூர் health assistant போல கேட்டு, தேவையான அடுத்த கேள்வியை மட்டும் கேட்கிறது, உரையாடலை doctor-ready summary ஆக மாற்றுகிறது.',
    whyCards: [
      ['Context-aware intake', 'அறிகுறி, வயது, மொழி, history, warning signs வைத்து Buddy கேள்விகள் கேட்கிறது.'],
      ['Doctor-ready summary', 'ஒவ்வொரு report லும் symptoms, severity, red flags, safe steps, doctor guidance இருக்கும்.'],
      ['உள்ளூர் மொழி வசதி', 'English, Telugu, Hindi, Tamil அல்லது mixed language ல் இயல்பாக பேசலாம்.'],
      ['Safety boundaries', 'Health Buddy diagnosis அல்லது prescription தராது. Serious symptoms இருந்தால் urgent care க்கு வழிகாட்டும்.'],
    ],
    useTitle: 'தினசரி நேரங்களில் Health Buddy உதவும்.',
    useSub: 'இரவு கவலையிலிருந்து doctor visit வரை, அடுத்த படி தெளிவாக இருக்கும்.',
    useCards: [
      ['எப்போது விரைவாக செயல்பட வேண்டும்', 'Chest pain, breathing trouble, fainting, bleeding, stroke signs urgent ஆக கருதப்படும்.'],
      ['சுத்தமான PDF வைத்துக்கொள்ளுங்கள்', 'Patients, agents, doctors memory க்கு பதிலாக PDF summary பயன்படுத்தலாம்.'],
      ['Doctor review க்கு தயார்', 'Duration, severity, medicines, history, questions தெளிவாக பேச report உதவும்.'],
      ['Useful history உருவாக்குங்கள்', 'Website மற்றும் WhatsApp reports phone number மூலம் இணைந்து past summaries காட்டும்.'],
      ['Field agents க்கு support', 'Agents WhatsApp தொடங்க, links open செய்ய, PDF download, doctor sharing க்கு உதவலாம்.'],
      ['எளிய கேள்விகள் கேளுங்கள்', 'Symptom என்ன அர்த்தம், என்ன track செய்ய வேண்டும், doctor க்கு என்ன சொல்ல வேண்டும் என்று கேட்கலாம்.'],
    ],
    safetyTitle: 'Private, consent-led, handoff க்காக கட்டப்பட்டது.',
    safetySub: 'Health Buddy patient control ஐ காக்கிறது. WhatsApp links signed/time-limited, website reports login தேவை, doctor access assigned referrals மட்டும்.',
    safetyItems: ['Report/PDF access க்கு signed WhatsApp links', 'Website patients க்கு login-based history', 'Assigned report review க்கு doctor portal', 'Clear disclaimer: diagnosis/prescription அல்ல'],
    capturesTitle: 'Report என்ன capture செய்கிறது.',
    captures: ['Chief complaint, duration, location, severity', 'Associated symptoms, red flags, urgency guidance', 'Past illness, medicines, allergies, habits சொல்லப்பட்டால்', 'Doctor review முன் safe comfort steps மற்றும் track செய்ய வேண்டியவை', 'WhatsApp அல்லது site ல் திறக்கக்கூடிய PDF summary'],
    waBot1: 'வணக்கம்! நான் Health Buddy. இன்று எப்படி உணர்கிறீர்கள்?',
    waUser: '2 நாட்களாக fever, tired.',
    waBot2: 'சரி, நான் இருக்கிறேன். cough அல்லது body pain இருக்கிறதா?',
    faqTitle: 'Patients மற்றும் partners கேட்கும் கேள்விகள்.',
    faqSub: 'Consultation தொடங்கும் முன் சுருக்கமான தெளிவான பதில்கள்.',
    faqs: [
      ['Health Buddy doctor ஆ?', 'இல்லை. இது AI intake assistant. Symptoms collect செய்து doctor-ready summary உருவாக்கும்.'],
      ['Prescription தருமா?', 'இல்லை. Dosage மற்றும் final diagnosis தராது. Doctors portal மூலம் review செய்யலாம்.'],
      ['WhatsApp மட்டும் போதுமா?', 'ஆம். WhatsApp ல் intake முடித்து report link மற்றும் PDF link பெறலாம்.'],
      ['Agents க்கு எப்படி உதவும்?', 'Agents WhatsApp flow, report link, PDF download, doctor connect ஆகியவற்றில் guide செய்யலாம்.'],
      ['Emergency ல் என்ன நடக்கும்?', 'Serious warning signs இருந்தால் urgent medical care அல்லது emergency services க்கு வழிகாட்டும்.'],
    ],
  },
};

const iconsForWhy = [Brain, ClipboardList, Languages, ShieldCheck];
const iconsForUseCases = [AlertTriangle, FileText, Stethoscope, TrendingUp, Users, HelpCircle];
const lc = (lang, key) => (LANDING_COPY[lang] && LANDING_COPY[lang][key]) || LANDING_COPY.en[key];
const WHATSAPP_NUMBER = '916301467800';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi Health Buddy')}`;

export default function Landing({ lang, setLang }) {
  return (
    <div className="relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="blob bg-[#8A9A86] w-[420px] h-[420px] -top-40 -right-32" />
      <div className="blob bg-[#E07A5F] w-[340px] h-[340px] top-[70vh] -left-24" />

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="fade-up">
            <Badge className="rounded-full bg-[#F4F1EA] text-[#1E3F33] border border-[#E8E1D5] hover:bg-[#F4F1EA] px-4 py-1.5 text-sm font-semibold">
              <Sparkles className="h-3.5 w-3.5 mr-1.5"/> {t(lang,'hero_kicker')}
            </Badge>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#1A1A1A] mt-5" data-testid="hero-title">
              {t(lang,'hero_title')}
            </h1>
            <p className="text-lg sm:text-xl text-[#4A4A4A] mt-6 max-w-xl leading-relaxed">
              {t(lang,'hero_sub')}
            </p>

            {/* Language chips */}
            <div className="mt-8">
              <div className="text-sm font-semibold text-[#7A7A7A] uppercase tracking-widest mb-3">{t(lang,'pick_lang')}</div>
              <div className="flex flex-wrap gap-3" data-testid="lang-chip-row">
                {LANGS.map(l => (
                  <button
                    key={l.code}
                    onClick={()=>setLang(l.code)}
                    data-testid={`lang-chip-${l.code}`}
                    className={`px-5 py-3 rounded-full border-2 text-lg font-semibold transition-all ${
                      lang===l.code
                        ? 'bg-[#1E3F33] text-white border-[#1E3F33] shadow-md'
                        : 'bg-white text-[#1E3F33] border-[#1E3F33] hover:bg-[#F4F1EA]'
                    }`}
                  >{l.label}</button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/chat">
                <Button data-testid="hero-cta-chat" className="h-14 px-8 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white text-lg font-semibold hb-shadow-lg">
                  {t(lang,'hero_cta')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                <Button data-testid="hero-cta-whatsapp" variant="outline" className="h-14 px-8 rounded-full border-2 border-[#1E3F33] text-[#1E3F33] text-lg font-semibold bg-white hover:bg-[#F4F1EA]">
                  <Phone className="mr-2 h-5 w-5"/> {t(lang,'hero_cta_2')}
                </Button>
              </a>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap gap-6 mt-10 text-[15px] text-[#4A4A4A]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#1E3F33]"/> {t(lang,'trust_1')}</span>
              <span className="flex items-center gap-2"><Languages className="h-5 w-5 text-[#1E3F33]"/> {t(lang,'trust_2')}</span>
              <span className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-[#1E3F33]"/> {t(lang,'trust_3')}</span>
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative fade-up">
            <div className="absolute -inset-6 bg-[#E8E1D5] rounded-[48px] -rotate-2" />
            <div className="relative rounded-[40px] overflow-hidden hb-shadow-lg border-4 border-white">
              <img
                src="https://images.unsplash.com/photo-1680778469498-a1da1cd98303?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                alt={lc(lang, 'heroAlt')}
                className="w-full h-[520px] object-cover"
              />
            </div>
            <Card className="absolute -bottom-6 -left-4 bg-white rounded-3xl p-4 hb-shadow-lg border border-[#E8E1D5] flex items-center gap-3 w-[260px]">
              <div className="h-12 w-12 rounded-2xl bg-[#1E3F33] grid place-items-center">
                <Heart className="h-6 w-6 text-[#E07A5F]" fill="#E07A5F"/>
              </div>
              <div>
                <div className="font-semibold text-[#1A1A1A]">{lc(lang, 'listeningTitle')}</div>
                <div className="text-sm text-[#4A4A4A]">{lc(lang, 'listeningSub')}</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* WHY HEALTH BUDDY IS DIFFERENT */}
      <section className="relative py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
            <div>
              <Badge className="rounded-full bg-[#F4F1EA] text-[#1E3F33] border border-[#E8E1D5] hover:bg-[#F4F1EA] px-4 py-1.5 text-sm font-semibold">
                {lc(lang, 'whyBadge')}
              </Badge>
              <h2 className="font-display text-4xl sm:text-5xl text-[#1A1A1A] mt-5 leading-tight">
                {lc(lang, 'whyTitle')}
              </h2>
              <p className="text-lg text-[#4A4A4A] mt-5 leading-relaxed">
                {lc(lang, 'whySub')}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {lc(lang, 'whyCards').map(([title, text], index) => {
                const Icon = iconsForWhy[index];
                return (
                <Card key={title} className="p-6 rounded-2xl border border-[#E8E1D5] bg-[#FAFAFA] hb-shadow-soft">
                  <div className="h-11 w-11 rounded-xl bg-[#1E3F33] grid place-items-center mb-4">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-display text-xl text-[#1A1A1A]">{title}</h3>
                  <p className="text-[15px] text-[#4A4A4A] mt-2 leading-relaxed">{text}</p>
                </Card>
              )})}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative bg-[#F4F1EA]/60 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]" data-testid="how-title">{t(lang,'how_title')}</h2>
            <p className="text-lg text-[#4A4A4A] mt-4">{t(lang,'how_sub')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: MessageCircle, t: t(lang,'step1_t'), d: t(lang,'step1_d'), n: '01' },
              { icon: Mic, t: t(lang,'step2_t'), d: t(lang,'step2_d'), n: '02' },
              { icon: FileText, t: t(lang,'step3_t'), d: t(lang,'step3_d'), n: '03' },
            ].map((s, i) => (
              <Card key={i} className="group relative p-8 rounded-3xl bg-white border border-[#E8E1D5] hover:-translate-y-1 transition-all hb-shadow-soft">
                <div className="font-display text-6xl text-[#E07A5F]/30 absolute right-5 top-3">{s.n}</div>
                <div className="h-14 w-14 rounded-2xl bg-[#1E3F33] grid place-items-center mb-5">
                  <s.icon className="h-7 w-7 text-white"/>
                </div>
                <h3 className="font-display text-2xl text-[#1A1A1A] mb-2">{s.t}</h3>
                <p className="text-[15px] text-[#4A4A4A] leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-20 sm:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
              {lc(lang, 'useTitle')}
            </h2>
            <p className="text-lg text-[#4A4A4A] mt-4">
              {lc(lang, 'useSub')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {lc(lang, 'useCards').map(([title, text], index) => {
              const Icon = iconsForUseCases[index];
              return (
              <Card key={title} className="p-6 rounded-2xl bg-white border border-[#E8E1D5] hb-shadow-soft">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-[#F4F1EA] grid place-items-center">
                    <Icon className="h-5 w-5 text-[#1E3F33]" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-[#1A1A1A]">{title}</h3>
                    <p className="text-[15px] text-[#4A4A4A] mt-2 leading-relaxed">{text}</p>
                  </div>
                </div>
              </Card>
            )})}
          </div>
        </div>
      </section>

      {/* RECORDS AND SAFETY */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="rounded-[28px] bg-[#1E3F33] text-white p-8 sm:p-10">
              <Lock className="h-8 w-8 text-[#E07A5F]" />
              <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-tight">
                {lc(lang, 'safetyTitle')}
              </h2>
              <p className="text-white/85 text-lg mt-4 leading-relaxed">
                {lc(lang, 'safetySub')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-8 text-sm">
                {lc(lang, 'safetyItems').map((item) => (
                  <div key={item} className="rounded-2xl bg-white/10 border border-white/15 p-4">{item}</div>
                ))}
              </div>
            </div>

            <div className="rounded-[28px] bg-[#F4F1EA] p-8 sm:p-10 border border-[#E8E1D5]">
              <h2 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] leading-tight">
                {lc(lang, 'capturesTitle')}
              </h2>
              <div className="mt-7 space-y-4">
                {lc(lang, 'captures').map((text) => (
                  <div key={text} className="flex items-start gap-3 text-[#1A1A1A]">
                    <ShieldCheck className="h-5 w-5 text-[#1E3F33] mt-0.5 shrink-0" />
                    <span className="text-[16px] leading-relaxed">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHATSAPP CALLOUT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-[32px] bg-[#1E3F33] text-white p-10 sm:p-14 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center overflow-hidden relative">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[#E07A5F]/20 blur-3xl"/>
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">{t(lang,'wa_title')}</h2>
            <p className="text-lg mt-4 text-white/85 max-w-xl">{t(lang,'wa_sub')}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
                <Button data-testid="wa-open-btn" className="rounded-full h-13 px-8 bg-[#E07A5F] hover:bg-[#C9674D] text-white text-lg font-semibold h-14">
                  <Phone className="mr-2 h-5 w-5"/> {t(lang,'wa_cta')}
                </Button>
              </a>
              <Link to="/chat">
                <Button variant="outline" className="rounded-full h-14 px-8 bg-transparent text-white border-2 border-white/40 hover:bg-white/10 text-lg font-semibold">
                  {t(lang,'hero_cta')}
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl bg-white/10 backdrop-blur-sm p-4 border border-white/20">
              <div className="rounded-2xl bg-[#F4F1EA] p-4 space-y-3 text-[#1A1A1A]">
                <div className="bg-white rounded-2xl rounded-tl-sm p-3 text-sm shadow-sm max-w-[85%]">{lc(lang, 'waBot1')}</div>
                <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-sm p-3 text-sm shadow-sm max-w-[85%] ml-auto">{lc(lang, 'waUser')}</div>
                <div className="bg-white rounded-2xl rounded-tl-sm p-3 text-sm shadow-sm max-w-[85%]">{lc(lang, 'waBot2')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]">{lc(lang, 'faqTitle')}</h2>
            <p className="text-lg text-[#4A4A4A] mt-4">{lc(lang, 'faqSub')}</p>
          </div>
          <div className="mt-10 grid gap-4">
            {lc(lang, 'faqs').map(([q, a]) => (
              <Card key={q} className="p-6 rounded-2xl border border-[#E8E1D5] bg-[#FAFAFA]">
                <h3 className="font-semibold text-lg text-[#1A1A1A]">{q}</h3>
                <p className="text-[15px] text-[#4A4A4A] mt-2 leading-relaxed">{a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
